import { Card } from "../ui/card";
import { formatDateTime } from "../../lib/format";
import { PartyPopper, AlertTriangle, BellRing, Users } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export const countByStatus = (requests) => ({
  pubblicata: requests?.filter((r) => r.status === "pubblicata").length || 0,
  assegnato: requests?.filter((r) => r.status === "assegnato").length || 0,
  completata: requests?.filter((r) => r.status === "completata").length || 0,
  da_fatturare: requests?.filter((r) => r.status === "da_fatturare").length || 0,
});

export const upcomingEventsOf = (requests) => {
  const now = new Date();
  const end = new Date(now.getTime() + 7 * 86400000);
  return (requests || [])
    .filter((r) => r.request_type === "evento" && !["annullata", "completata", "da_fatturare"].includes(r.status))
    .map((r) => ({ r, d: new Date(String(r.operative?.data_ora_evento || "").replace(" ", "T")) }))
    .filter(({ d }) => !isNaN(d.getTime()) && d >= now && d <= end)
    .sort((a, b) => a.d - b.d)
    .map(({ r, d }) => ({ ...r, _urgent: !r.assigned_partner_id && d.getTime() - now.getTime() < 48 * 3600000 }));
};

export const StatsCard = ({ counts }) => (
  <Card data-testid="stats-compact-card" className="p-5 border-slate-200 divide-y divide-slate-100">
    {[
      ["Pubblicate", counts.pubblicata, "text-[#A88E56]", "bg-[#C6AD76]"],
      ["Assegnate", counts.assegnato, "text-[#18314F]", "bg-[#18314F]"],
      ["Completate", counts.completata, "text-[#15803D]", "bg-[#16A34A]"],
      ["Fatturare", counts.da_fatturare, "text-[#942F30]", "bg-[#942F30]"],
    ].map(([label, value, tone, dot]) => (
      <div key={label} data-testid={`stat-${label.toLowerCase()}`} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
        <span className="flex items-center gap-2 nx-label"><span className={`h-2 w-2 rounded-full ${dot}`} />{label}</span>
        <span className={`text-2xl font-bold font-heading ${tone}`}>{value}</span>
      </div>
    ))}
  </Card>
);

export const CandidatesAlertCard = ({ total, onOpen, onDismiss }) => (
  <Card data-testid="candidates-alert-card" className="p-5 border-amber-300 bg-amber-50/70 cursor-pointer hover:bg-amber-50" onClick={onOpen}>
    <div className="flex items-center justify-between">
      <p className="nx-label">Nuove candidature</p>
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
      </span>
    </div>
    <p data-testid="candidates-alert-count" className="text-3xl font-bold font-heading mt-1 text-amber-700">{total}</p>
    <p className="text-[11px] text-slate-600 mt-0.5">partner si sono candidati da quando sei qui</p>
    <button type="button" data-testid="candidates-alert-dismiss" className="text-[11px] text-amber-700 hover:underline mt-2" onClick={(e) => { e.stopPropagation(); onDismiss(); }}>Segna come viste</button>
  </Card>
);

export const UpcomingEventsCard = ({ events, onOpen, onSollecita, onFilterEvents }) => {
  const urgentCount = events.filter((r) => r._urgent).length;
  return (
    <Card data-testid="upcoming-events-card" className={`p-5 ${urgentCount > 0 ? "border-red-300 bg-red-50/60" : "border-slate-200 bg-[#F2F4F5]"}`}>
      <div className="flex items-center justify-between">
        <p className="nx-label">Eventi in programma</p>
        {urgentCount > 0 ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <PartyPopper className="h-4 w-4 text-[#942F30]" />}
      </div>
      <p data-testid="upcoming-events-count" className="text-3xl font-bold font-heading mt-1 text-[#942F30]">{events.length}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">
        prossimi 7 giorni
        {urgentCount > 0 && <span data-testid="upcoming-events-urgent" className="ml-2 font-semibold text-red-600">· {urgentCount} da assegnare entro 48h</span>}
      </p>
      {events.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-[#D48E8E] pt-2">
          {events.slice(0, 3).map((r) => (
            <li key={r.id} data-testid={`upcoming-event-${r.id}`} onClick={() => onOpen(r)}
              className={`text-xs flex items-center justify-between gap-2 cursor-pointer hover:opacity-80 ${r._urgent ? "text-red-700 font-semibold" : "text-slate-700"}`}>
              <span className="truncate font-medium flex items-center gap-1">
                {r._urgent && <AlertTriangle className="h-3 w-3 shrink-0 text-red-600" />}
                {r.evento?.denominazione || r.code}
              </span>
              <span className="shrink-0 flex items-center gap-1">
                <span className={r._urgent ? "text-red-600" : "text-slate-500"}>{formatDateTime(r.operative?.data_ora_evento)}</span>
                {r._urgent && r.status === "pubblicata" && (
                  <button type="button" data-testid={`card-sollecita-${r.id}`} title="Sollecita partner" className="p-0.5 rounded hover:bg-red-100 text-red-600" onClick={(e) => onSollecita(e, r)}>
                    <BellRing className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </li>
          ))}
          {events.length > 3 && <li className="text-[11px] text-[#942F30] cursor-pointer hover:underline" onClick={onFilterEvents}>+{events.length - 3} altri → filtra Eventi</li>}
        </ul>
      )}
    </Card>
  );
};

export const withCandidates = (requests) =>
  (requests || []).filter((r) => r.status === "pubblicata" && (r.candidates || []).length > 0)
    .sort((a, b) => (b.candidates?.length || 0) - (a.candidates?.length || 0));

export const CandidaturesCard = ({ items, onOpen }) => (
  <TooltipProvider delayDuration={150}>
  <Card data-testid="candidatures-card" className="p-5 border-slate-200">
    <div className="flex items-center justify-between">
      <p className="nx-label">Candidature partner</p>
      <Users className="h-4 w-4 text-primary" />
    </div>
    <p data-testid="candidatures-count" className="text-3xl font-bold font-heading mt-1 text-primary">{items.length}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">richieste con partner candidati</p>
    {items.length > 0 && (
      <ul className="mt-3 space-y-1.5 border-t border-slate-200 pt-2 max-h-40 overflow-auto">
        {items.map((r) => (
          <li key={r.id} data-testid={`candidature-item-${r.id}`} className="flex items-center justify-between gap-2 text-xs">
            <button type="button" data-testid={`candidature-code-${r.id}`} onClick={() => onOpen(r)}
              className="font-mono font-semibold text-primary hover:text-nexus-gold hover:underline">{r.code}</button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span data-testid={`candidature-n-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold cursor-default">
                  <Users className="h-3 w-3" /> {r.candidates.length}
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" data-testid={`candidature-names-${r.id}`} className="max-w-xs">
                <p className="text-[10px] uppercase tracking-wide opacity-70 mb-1">Partner candidati</p>
                <ul className="space-y-0.5">{(r.candidates_detail || []).map((c) => <li key={c.id}>{c.name}</li>)}</ul>
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>
    )}
  </Card>
  </TooltipProvider>
);
